import { format, parseISO, parse } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { ColumnDef } from '@tanstack/react-table'
// import { ArrowRight } from 'lucide-react'
import { rrulestr } from 'rrule'
import { Button } from '~/ui/button'
import { useNavigate } from 'react-router-dom'

export type TrackColumns = {
  id: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  rrule: string
  location: any
  zone: any
  playlist?: any
  dateRange?: string
  schedule?: string
}

const getHumanReadableTimezone = (tz?: string) => {
  if (!tz) return ''

  try {
    // Get timezone abbreviation
    const now = new Date()
    const tzAbbr = formatInTimeZone(now, tz, 'zzz') // Returns 'EST', 'PST', etc.
    return tzAbbr
  } catch {
    return tz.split('/').pop()?.replace(/_/g, ' ') || ''
  }
}

const parseRRuleToEnglish = (
  rrule: string,
  startDate: Date,
  startTime: Date,
  endTime: Date,
  timeZone?: string
): string => {
  if (!rrule) return ''

  try {
    const parsedRRule = rrulestr(rrule)

    // Get the basic recurrence text from rrule
    const basicText = parsedRRule.toText()

    // Format the start date and times
    const startDateString = format(startDate, 'MMMM do, yyyy')
    const startTimeString = format(startTime, 'h:mm a')
    const endTimeString = format(endTime, 'h:mm a')

    const timezone = getHumanReadableTimezone(timeZone)

    // Create more verbose description
    let verboseText = ''

    if (basicText.toLowerCase().includes('daily')) {
      verboseText = `Every day starting on ${startDateString} from ${startTimeString} to ${endTimeString} ${timezone}`
    } else if (basicText.toLowerCase().includes('weekly')) {
      if (basicText.toLowerCase().includes('weekday')) {
        verboseText = `Every weekday starting on ${startDateString} from ${startTimeString} to ${endTimeString} ${timezone}`
      } else {
        // Extract specific days from the basic text
        const dayMatch = basicText.match(/on ([^,]+(?:, [^,]+)*)/i)
        const days = dayMatch ? dayMatch[1] : 'selected days'
        verboseText = `Every week on ${days} starting ${startDateString} from ${startTimeString} to ${endTimeString} ${timezone}`
      }
    } else if (basicText.toLowerCase().includes('monthly')) {
      verboseText = `Every month starting on ${startDateString} from ${startTimeString} to ${endTimeString} ${timezone}`
    } else if (basicText.toLowerCase().includes('yearly')) {
      verboseText = `Every year starting on ${startDateString} from ${startTimeString} to ${endTimeString} ${timezone}`
    } else {
      // Fallback to enhanced basic text
      verboseText = `${basicText} starting on ${startDateString} from ${startTimeString} to ${endTimeString} ${timezone}`
    }

    return verboseText
  } catch (error) {
    console.error('Error parsing RRule:', error)
    return ''
  }
}

export const columns: ColumnDef<TrackColumns | null | undefined>[] = [
  {
    id: 'location',
    header: 'Location',
    cell: ({ row }) => {
      return (
        <>
          <div className="text-sm">{row.original?.zone?.location?.name}</div>
        </>
      )
    },
  },
  {
    id: 'zone',
    header: 'Zone',
    cell: ({ row }) => {
      return (
        <>
          <div className="text-sm">{row.original?.zone?.name}</div>
        </>
      )
    },
  },
  {
    id: 'playlist',
    header: 'Playlist',
    cell: ({ row }) => {
      return (
        <>
          <div className="text-sm">{row.original?.playlist?.name}</div>
        </>
      )
    },
  },
  {
    id: 'dateRange',
    header: () => (
      <div className="flex items-center">
        {/* Start <ArrowRight className="mx-1.5 h-3 w-3" /> End */}
        Schedule
      </div>
    ),
    cell: ({ row }) => {
      const startDateValue = row.original?.startDate
      const endDateValue = row.original?.endDate
      const startTimeValue = row.original?.startTime
      const endTimeValue = row.original?.endTime
      const rruleValue = row.original?.rrule

      if (!startDateValue || !startTimeValue || !endTimeValue) {
        return <div className="text-sm">Invalid date/time</div>
      }

      try {
        const startDate = parseISO(startDateValue)
        const startTime = parse(startTimeValue, 'HH:mm', new Date())
        const endTime = parse(endTimeValue, 'HH:mm', new Date())

        const startDateString = format(startDate, 'MM/dd/yyyy')
        const startTimeString = format(startTime, 'h:mm a')
        const endTimeString = format(endTime, 'h:mm a')

        // Case 1: Both start and end dates exist
        if (endDateValue) {
          const endDate = parseISO(endDateValue)
          const endDateString = format(endDate, 'MM/dd/yyyy')

          return (
            <div className="text-sm">
              <div>
                {startDateString} {startTimeString} - {endDateString} {endTimeString}
              </div>
            </div>
          )
        }

        // Case 2: Only start date, parse RRule for recurrence
        let rruleText = ''
        if (rruleValue) {
          rruleText = parseRRuleToEnglish(
            rruleValue,
            startDate,
            startTime,
            endTime,
            row.original?.zone?.location?.timeZone
          )
        }

        return (
          <div className="max-w-[300px] text-sm">
            {rruleText ? (
              <div className="break-words">{rruleText}</div>
            ) : (
              <div>
                {startDateString} {startTimeString} - {endTimeString}
              </div>
            )}
          </div>
        )
      } catch (error) {
        console.error('Date parsing error:', error)
        return <div className="text-sm">Invalid date format</div>
      }
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const navigate = useNavigate()
      return (
        <>
          <div className="flex items-center justify-start">
            <Button
              variant="link"
              size="sm"
              className="cursor-pointer p-0 text-xs"
              onClick={() => {
                navigate(`/schedules/${row.original?.id}/edit`)
              }}
            >
              Edit
            </Button>
          </div>
        </>
      )
    },
  },
]
